import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import diiLogo from "@/assets/dii-logo.png";
import { API_BASE } from "@/lib/backend";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validate = (): boolean => {
    if (newPassword.length < 8) {
      setValidationError("Password must be at least 8 characters.");
      return false;
    }
    if (newPassword !== newPassword.trim()) {
      setValidationError("Password must not have leading or trailing spaces.");
      return false;
    }
    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match.");
      return false;
    }
    setValidationError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password/reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      if (res.ok) {
        setSuccess(true);
      } else if (res.status === 422) {
        const data = await res.json().catch(() => null);
        setError(data?.detail ?? "Invalid password.");
      } else {
        setError(
          "This reset link is invalid or has expired. Please request a new one."
        );
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const noToken = !token;

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/20" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-primary-foreground">
          <div />
          <div className="space-y-6">
            <h1 className="text-4xl font-bold leading-tight">Reset Password</h1>
            <p className="text-lg opacity-90 max-w-md">
              Set a new password for your Dilli account.
            </p>
          </div>
          <p className="text-sm opacity-60">
            &copy; {new Date().getFullYear()} Dilli. All rights reserved.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center space-y-2">
            <img src={diiLogo} alt="Dilli" className="h-12 mx-auto" />
            <h2 className="text-2xl font-bold">Reset Password</h2>
          </div>

          {noToken ? (
            <div className="text-center space-y-4">
              <p className="text-destructive">
                No reset token found. Please use the link from your email.
              </p>
              <Link to="/auth">
                <Button variant="outline" className="mt-4">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-4">
              <p className="text-green-600 font-medium">
                Your password has been reset successfully.
              </p>
              <Link to="/auth">
                <Button className="mt-4">Back to Sign In</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}
              {validationError && (
                <p className="text-sm text-destructive text-center">
                  {validationError}
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setValidationError(null);
                    }}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setValidationError(null);
                  }}
                  required
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              <div className="text-center">
                <Link
                  to="/auth"
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
